import { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import {
  FilterCategoryFields,
  getSearchInfoForTracking,
  ProductData,
  SearchInfo,
} from '@oneaudi/vtp-shared';

const getCurrentDomain = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

const getEventName = (ctaType: string, eventName: string): string => {
  let parsedEventName = '';
  switch (ctaType) {
    case 'contact':
    case 'nws':
    case 'bevAgency':
      parsedEventName = `${eventName} - click on contact`;
      break;
    case 'ecom':
      parsedEventName = `${eventName} - click on oneaudi shop`;
      break;
    case 'phone':
    case 'central-customer-hotline':
      parsedEventName = `${eventName} - call dealer`;
      break;
    default:
      parsedEventName = `${eventName} - click on generic cta`;
  }
  return parsedEventName;
};
interface FeatureAppNames {
  eventName: string;
  componentName: string;
}
export const getFeatureAppNames = (): FeatureAppNames => {
  return {
    eventName: 'vtp product list page',
    componentName: 'vtp-plp',
  };
};

export const trackImageChange = (
  trackingService: TrackingServiceV2,
  elementName: 'arrow' | 'image' | 'other',
  label: string,
  pos: string,
  relatedProduct: ProductData,
  actionType: 'swipe' | 'click' | 'automatic',
  clickID: string
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'navigation',
          eventName: `vtp results - change gallery image`,
        },
        attributes: {
          componentName: 'vtp-results',
          actionType,
          label,
          targetURL: '',
          clickID,
          elementName,
          value: '',
          pos,
          relatedProduct,
        },
      },
    });
  }
};
export const trackSliderChange = (
  trackingService: TrackingServiceV2,
  elementName: 'arrow' | 'text link' | 'other'
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: 'vtp suggested cars - navigate',
        },
        attributes: {
          componentName: 'vtp-suggested-cars',
          actionType: 'click',
          label: '',
          targetURL: '',
          clickID: '',
          elementName,
          value: '',
          pos: '',
        },
      },
    });
  }
};

export const trackViewChange = (
  trackingService: TrackingServiceV2,
  viewType: 'list' | 'grid',
  label: string
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: viewType === 'list' ? 'vtp results - list view' : 'vtp results - grid view',
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          targetURL: '',
          clickID: '',
          elementName: 'text link',
          value: '',
          pos: '',
        },
      },
      componentUpdate: {
        attributes: {
          viewType,
        },
      },
    });
  }
};

export const trackSorting = (trackingService: TrackingServiceV2, label: string, value: string) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: 'vtp results - sort',
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          targetURL: '',
          clickID: '',
          elementName: 'drop down',
          value,
          pos: '',
        },
      },
      componentUpdate: {
        attributes: {
          sortingOption: value,
        },
      },
    });
  }
};

export const trackDetailsPageNav = (
  trackingService: TrackingServiceV2,
  label: string | any,
  targetURL: string,
  elementName: string,
  value: string
) => {
  const completeURL = `${getCurrentDomain()}${targetURL}`;
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'internal_link',
          eventName: `vtp results - go to detail page`,
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          actionType: 'click',
          targetURL: completeURL,
          clickID: '',
          elementName,
          value,
          pos: '',
        },
      },
    });
  }
};

export const trackAddToFav = (
  trackingService: TrackingServiceV2,
  label: string,
  relatedProduct: ProductData
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'favorite',
          eventName: `vtp results - add to favorites`,
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: '',
          pos: '',
          relatedProduct,
        },
      },
    });
  }
};

export const trackRemoveFromFav = (
  trackingService: TrackingServiceV2,
  label: string,
  relatedProduct: ProductData
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'favorite',
          eventName: `vtp results - remove from favorites`,
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          targetURL: '',
          clickID: '',
          elementName: 'icon',
          value: '',
          pos: '',
          relatedProduct,
        },
      },
    });
  }
};

export const trackTabChange = (
  trackingService: TrackingServiceV2,
  label: string,
  relatedProduct?: ProductData
) => {
  const { eventName, componentName } = getFeatureAppNames();
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: `${eventName} - tab click`,
        },
        attributes: relatedProduct
          ? {
              componentName,
              label,
              targetURL: '',
              clickID: '',
              elementName: 'tab',
              value: '',
              pos: '',
              relatedProduct,
              actionType: 'click',
            }
          : {
              componentName,
              label,
              targetURL: '',
              clickID: '',
              elementName: 'tab',
              value: '',
              pos: '',
              actionType: 'click',
            },
      },
    });
  }
};
const getEcomEventAction = (targetURL: string) =>
  targetURL.startsWith(window.location.origin) ? 'internal_link' : 'exit_link';
export const trackCTAClick = (
  trackingService: TrackingServiceV2,
  eventAction: 'call' | 'internal_link' | 'exit_link',
  type: string,
  label: string,
  targetURL: string,
  relatedProduct: ProductData
) => {
  const { eventName, componentName } = getFeatureAppNames();
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: type === 'ecom' ? getEcomEventAction(targetURL) : eventAction,
          eventName: getEventName(type, eventName),
        },
        attributes: {
          componentName,
          label,
          targetURL,
          clickID: '',
          elementName: 'text link',
          value: '',
          pos: '',
          relatedProduct,
        },
      },
    });
  }
};

export const trackLayerLinkClick = (
  trackingService: TrackingServiceV2,
  label: string,
  elementName: 'text link' | 'icon',
  relatedProduct: ProductData
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'internal_link',
          eventName: `vtp results  - open modal layer `,
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          targetURL: '',
          clickID: '',
          elementName,
          value: '',
          pos: '',
          relatedProduct,
          actionType: 'click',
        },
      },
    });
  }
};
export const trackLayerOpen = (
  trackingService: TrackingServiceV2,
  eventType: string,
  pageID?: string,
  promotionName?: string
) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'view_change',
          eventName: `vtp results  - ${eventType}`,
        },
        attributes: {
          componentName: 'vtp-results',
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: '',
          pos: '',
        },
      },
      componentUpdate: {
        page: {
          pageInfo: {
            pageID,
          },
          attributes: {
            promotionName,
          },
        },
      },
    });
  }
};
export const trackLayerClose = (trackingService: TrackingServiceV2, value: string) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: `vtp results  - close modal layer`,
        },
        attributes: {
          componentName: 'vtp-results',
          label: '',
          targetURL: '',
          clickID: '',
          elementName: 'text link',
          value,
          pos: '',
        },
      },
      componentUpdate: {
        page: {
          pageInfo: {
            pageID: '',
          },
          attributes: {
            promotionName: '',
          },
        },
      },
    });
  }
};

export const trackFeatureAppReady = (
  trackingService: TrackingServiceV2,
  isNewCarsPage: boolean,
  filterCategories: FilterCategoryFields[] | undefined
) => {
  const { eventName, componentName } = getFeatureAppNames();
  const searchInfo: SearchInfo = getSearchInfoForTracking();
  // eslint-disable-next-line no-param-reassign
  trackingService.featureAppName = __FEATURE_APP_NAME__;

  const availableCategories: string[] = [];
  if (filterCategories) {
    filterCategories.forEach((filterCategory) => {
      if (filterCategory?.fields?.filterCategory)
        availableCategories.push(filterCategory?.fields?.filterCategory);
    });
    availableCategories.sort();
  }

  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'feature_app_ready',
          eventName: `${eventName}`,
        },
        attributes: {
          componentName,
          label: '',
          targetURL: '',
          clickID: '',
          elementName: '',
          value: '',
          pos: '',
        },
      },
      componentUpdate: {
        attributes: {
          implementer: 2,
          availableCategories,
          sortingOption: searchInfo.sortingOption,
          search: {
            name: isNewCarsPage ? 'vtp search - new' : 'vtp search - used',
            term: '',
            results: searchInfo.totalCount,
            filter: searchInfo.filterArray,
          },
          viewType: 'grid',
        },
      },
    });
  }
};

export const trackLoadMoreVehicles = (
  trackingService: TrackingServiceV2,
  label: string,
  value: number
) => {
  const { eventName, componentName } = getFeatureAppNames();
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: `${eventName} - show more cars`,
        },
        attributes: {
          componentName,
          label,
          targetURL: '',
          clickID: '',
          elementName: 'button',
          value,
          pos: '',
        },
      },
    });
  }
};

export const trackBackLinkClick = (trackingService: TrackingServiceV2, label: string) => {
  if (trackingService) {
    trackingService.track({
      event: {
        eventInfo: {
          eventAction: 'content',
          eventName: `vtp results - click to go back`,
        },
        attributes: {
          componentName: 'vtp-results',
          label,
          targetURL: '',
          clickID: '',
          elementName: 'text link',
          value: '',
          pos: '',
        },
      },
    });
  }
};
